// frontend/src/i18n/index.ts — i18next setup.
//
// Loaded once at app boot from main.tsx. Per CONVENTIONS §i18n:
//   - Default English (`en`); second language French (`fr`).
//   - Namespace per entity. Current namespaces: `common`, `errors`, `auth`
//     (login + MFA + change-password + profile), and `nav` (the app-shell
//     navigation: domain + admin-section labels, switcher/account-menu copy,
//     ComingSoon). `nav` is cross-cutting like `common`/`errors`, so its
//     `_meta.owner_module` is the i18n directory itself. Future per-module
//     namespaces (sites, users, etc.) land with their feature.
//   - `escapeValue: false` — React already escapes (xss-safe by render).
//   - `defaultNS: 'common'` — `t('foo')` resolves under common; cross-NS uses
//     `t('errors:GENERIC')` colon form OR `useTranslation('auth')`.
//   - User language pref synced from `me.user.language` via
//     `useLanguageSync()` hook — runs inside QueryClientProvider, fires
//     `i18n.changeLanguage(lang)` on data change.

import { LANGUAGES } from '@ems-app/schemas/common/language';
import { FAMILY_LABELS, FOLDER_LABELS, TOPIC_TAIL_LABELS } from '@ems-app/schemas/files/vocabulary';
import {
  ASSET_CATEGORIES,
  CATEGORY_SECTION_KEYS,
  SECTION_LABELS,
  categoryLabel,
} from '@ems-app/schemas/assets/categories';
import {
  ASSET_CONDITION_LABELS,
  ASSET_EVENT_LABELS,
  ASSET_EVENT_REASON_LABELS,
  ASSET_STATUS_LABELS,
  BOOK_STATUS_LABELS,
} from '@ems-app/schemas/assets/vocabulary';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { installValidationMessages } from '../lib/validation.js';

import enAuth from './locales/en/auth.json';
import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import enNav from './locales/en/nav.json';
import enUsers from './locales/en/users.json';
import enRoles from './locales/en/roles.json';
import enSiteTypes from './locales/en/site-types.json';
import enAssetOwners from './locales/en/asset-owners.json';
import enResponsibilities from './locales/en/responsibilities.json';
import enZones from './locales/en/zones.json';
import enClusters from './locales/en/clusters.json';
import enImportExport from './locales/en/import-export.json';
import enInvoicing from './locales/en/invoicing.json';
import enAudit from './locales/en/audit.json';
import frAuth from './locales/fr/auth.json';
import frCommon from './locales/fr/common.json';
import frErrors from './locales/fr/errors.json';
import frNav from './locales/fr/nav.json';
import frUsers from './locales/fr/users.json';
import frRoles from './locales/fr/roles.json';
import frSiteTypes from './locales/fr/site-types.json';
import frAssetOwners from './locales/fr/asset-owners.json';
import frResponsibilities from './locales/fr/responsibilities.json';
import frZones from './locales/fr/zones.json';
import frClusters from './locales/fr/clusters.json';
import frImportExport from './locales/fr/import-export.json';
import frInvoicing from './locales/fr/invoicing.json';
import frAudit from './locales/fr/audit.json';
import arAuth from './locales/ar/auth.json';
import arCommon from './locales/ar/common.json';
import arErrors from './locales/ar/errors.json';
import arNav from './locales/ar/nav.json';
import arUsers from './locales/ar/users.json';
import arRoles from './locales/ar/roles.json';
import arSiteTypes from './locales/ar/site-types.json';
import arAssetOwners from './locales/ar/asset-owners.json';
import arResponsibilities from './locales/ar/responsibilities.json';
import arZones from './locales/ar/zones.json';
import arClusters from './locales/ar/clusters.json';
import arImportExport from './locales/ar/import-export.json';
import arInvoicing from './locales/ar/invoicing.json';
import arAudit from './locales/ar/audit.json';
import enAdministrativeAreas from './locales/en/administrative-areas.json';
import frAdministrativeAreas from './locales/fr/administrative-areas.json';
import arAdministrativeAreas from './locales/ar/administrative-areas.json';
import enSso from './locales/en/sso.json';
import frSso from './locales/fr/sso.json';
import arSso from './locales/ar/sso.json';
import enApiClients from './locales/en/api-clients.json';
import frApiClients from './locales/fr/api-clients.json';
import arApiClients from './locales/ar/api-clients.json';
import enPermissions from './locales/en/permissions.json';
import frPermissions from './locales/fr/permissions.json';
import arPermissions from './locales/ar/permissions.json';
import enTokens from './locales/en/tokens.json';
import frTokens from './locales/fr/tokens.json';
import arTokens from './locales/ar/tokens.json';
import enAssets from './locales/en/assets.json';
import frAssets from './locales/fr/assets.json';
import arAssets from './locales/ar/assets.json';
import enCatalog from './locales/en/catalog.json';
import frCatalog from './locales/fr/catalog.json';
import arCatalog from './locales/ar/catalog.json';
import enConsumables from './locales/en/consumables.json';
import frConsumables from './locales/fr/consumables.json';
import arConsumables from './locales/ar/consumables.json';
import enPlatformUsers from './locales/en/platform-users.json';
import frPlatformUsers from './locales/fr/platform-users.json';
import arPlatformUsers from './locales/ar/platform-users.json';
import enPlatformProjects from './locales/en/platform-projects.json';
import frPlatformProjects from './locales/fr/platform-projects.json';
import arPlatformProjects from './locales/ar/platform-projects.json';
import enIntegrations from './locales/en/integrations.json';
import frIntegrations from './locales/fr/integrations.json';
import arIntegrations from './locales/ar/integrations.json';
import enProjectSettings from './locales/en/project-settings.json';
import frProjectSettings from './locales/fr/project-settings.json';
import arProjectSettings from './locales/ar/project-settings.json';
import enSitePriorities from './locales/en/site-priorities.json';
import frSitePriorities from './locales/fr/site-priorities.json';
import arSitePriorities from './locales/ar/site-priorities.json';
import enSiteCategories from './locales/en/site-categories.json';
import frSiteCategories from './locales/fr/site-categories.json';
import arSiteCategories from './locales/ar/site-categories.json';
import enSiteTags from './locales/en/site-tags.json';
import frSiteTags from './locales/fr/site-tags.json';
import arSiteTags from './locales/ar/site-tags.json';
import enImports from './locales/en/imports.json';
import frImports from './locales/fr/imports.json';
import arImports from './locales/ar/imports.json';
import enSites from './locales/en/sites.json';
import frSites from './locales/fr/sites.json';
import arSites from './locales/ar/sites.json';
import enFiles from './locales/en/files.json';
import frFiles from './locales/fr/files.json';
import arFiles from './locales/ar/files.json';
import enSavedViews from './locales/en/saved-views.json';
import frSavedViews from './locales/fr/saved-views.json';
import arSavedViews from './locales/ar/saved-views.json';
import enTickets from './locales/en/tickets.json';
import frTickets from './locales/fr/tickets.json';
import arTickets from './locales/ar/tickets.json';
import enTeams from './locales/en/teams.json';
import frTeams from './locales/fr/teams.json';
import arTeams from './locales/ar/teams.json';

// Initial language = the last-known locale cached in localStorage — the same
// write-through `theme-init.js` reads to set <html dir> before paint, so the
// app *boots* in the right language (not just the right direction). Server-
// authoritative: useLanguageSync corrects it from /me on load. Falls back to
// 'en' when absent / invalid / unavailable (private mode). NOT browser language
// detection — we only trust a value we previously wrote (CONVENTIONS §i18n).
function readCachedLocale(): string {
  try {
    const v = localStorage.getItem('locale');
    if (v && (LANGUAGES as readonly string[]).includes(v)) return v;
  } catch {
    /* localStorage blocked (private mode) — fall through to default */
  }
  return 'en';
}

await i18n.use(initReactI18next).init({
  lng: readCachedLocale(),
  fallbackLng: 'en',
  // Arabic (`ar`) is RTL + in scope for the Saudi market (CONVENTIONS §i18n).
  // Language set shared with the wire contracts — single source in
  // @ems-app/schemas/common/language (ADR-009; CONVENTIONS §i18n).
  supportedLngs: [...LANGUAGES],
  ns: ['common', 'errors', 'auth', 'nav', 'users', 'roles', 'site-types', 'site-priorities', 'site-categories', 'site-tags', 'sites', 'saved-views', 'responsibilities', 'zones', 'clusters', 'administrative-areas', 'import-export', 'invoicing', 'imports', 'audit', 'sso', 'api-clients', 'permissions', 'platform-users', 'platform-projects', 'project-settings', 'integrations', 'tokens', 'assets', 'catalog', 'consumables', 'files', 'tickets', 'teams', 'asset-owners'],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  resources: {
    en: { common: enCommon, errors: enErrors, auth: enAuth, nav: enNav, users: enUsers, roles: enRoles, 'site-types': enSiteTypes, 'site-priorities': enSitePriorities, 'site-categories': enSiteCategories, 'site-tags': enSiteTags, sites: enSites, 'saved-views': enSavedViews, responsibilities: enResponsibilities, zones: enZones, clusters: enClusters, 'administrative-areas': enAdministrativeAreas, 'import-export': enImportExport, invoicing: enInvoicing, imports: enImports, audit: enAudit, sso: enSso, 'api-clients': enApiClients, permissions: enPermissions, 'platform-users': enPlatformUsers, 'platform-projects': enPlatformProjects, 'project-settings': enProjectSettings, integrations: enIntegrations, tokens: enTokens, assets: enAssets, catalog: enCatalog, consumables: enConsumables, files: enFiles, tickets: enTickets, teams: enTeams, 'asset-owners': enAssetOwners },
    fr: { common: frCommon, errors: frErrors, auth: frAuth, nav: frNav, users: frUsers, roles: frRoles, 'site-types': frSiteTypes, 'site-priorities': frSitePriorities, 'site-categories': frSiteCategories, 'site-tags': frSiteTags, sites: frSites, 'saved-views': frSavedViews, responsibilities: frResponsibilities, zones: frZones, clusters: frClusters, 'administrative-areas': frAdministrativeAreas, 'import-export': frImportExport, invoicing: frInvoicing, imports: frImports, audit: frAudit, sso: frSso, 'api-clients': frApiClients, permissions: frPermissions, 'platform-users': frPlatformUsers, 'platform-projects': frPlatformProjects, 'project-settings': frProjectSettings, integrations: frIntegrations, tokens: frTokens, assets: frAssets, catalog: frCatalog, consumables: frConsumables, files: frFiles, tickets: frTickets, teams: frTeams, 'asset-owners': frAssetOwners },
    ar: { common: arCommon, errors: arErrors, auth: arAuth, nav: arNav, users: arUsers, roles: arRoles, 'site-types': arSiteTypes, 'site-priorities': arSitePriorities, 'site-categories': arSiteCategories, 'site-tags': arSiteTags, sites: arSites, 'saved-views': arSavedViews, responsibilities: arResponsibilities, zones: arZones, clusters: arClusters, 'administrative-areas': arAdministrativeAreas, 'import-export': arImportExport, invoicing: arInvoicing, imports: arImports, audit: arAudit, sso: arSso, 'api-clients': arApiClients, permissions: arPermissions, 'platform-users': arPlatformUsers, 'platform-projects': arPlatformProjects, 'project-settings': arProjectSettings, integrations: arIntegrations, tokens: arTokens, assets: arAssets, catalog: arCatalog, consumables: arConsumables, files: arFiles, tickets: arTickets, teams: arTeams, 'asset-owners': arAssetOwners },
  },
});

// ── DOMAIN VOCABULARY injection (the charter's vocabulary law) ───────────────
// Domain nouns live INLINE in the shared package's registries (one home, typed,
// readable by both ends); i18next stays the DELIVERY path, so call sites keep
// `t('category.x')` while the words have exactly one source. Locale JSONs carry
// PROSE only — a category/section key in a locale file would shadow-drift and
// is deliberately absent.
for (const lng of LANGUAGES) {
  const cats = Object.fromEntries(ASSET_CATEGORIES.map((c) => [c, categoryLabel(c, lng)]));
  const sections = Object.fromEntries(
    CATEGORY_SECTION_KEYS.map((s) => [s, SECTION_LABELS[s][lng as 'en' | 'fr' | 'ar']]),
  );
  const l = lng as 'en' | 'fr' | 'ar';
  const words = <T extends Record<string, { en: string; fr: string; ar: string }>>(m: T) =>
    Object.fromEntries(Object.entries(m).map(([k, v]) => [k, v[l]]));
  i18n.addResourceBundle(
    lng,
    'assets',
    {
      category: cats,
      form: { grid: sections },
      status: words(ASSET_STATUS_LABELS),
      condition: words(ASSET_CONDITION_LABELS),
      bookStatus: words(BOOK_STATUS_LABELS),
      event: words(ASSET_EVENT_LABELS),
      reason: words(ASSET_EVENT_REASON_LABELS),
    },
    true,
    true,
  );
  i18n.addResourceBundle(lng, 'catalog', { category: cats, form: { grid: sections } }, true, true);
  // The files vocabulary — the folder words, the family words, the topic tail
  // (a category topic resolves through the assets registry above).
  i18n.addResourceBundle(lng, 'files', { folders: words(FOLDER_LABELS), family: words(FAMILY_LABELS), topics: words(TOPIC_TAIL_LABELS) }, true, true);
}

// Direction is locale-driven (DESIGN-TOKENS §Responsive #6): set <html dir> from
// the active language — `rtl` for ar — the same mechanism as the `.dark` flip.
// i18next.dir() knows ar/he/fa/ur are RTL. Re-applied on every language change
// (e.g. when useLanguageSync fires changeLanguage from me.user.language).
function applyDirection(lng: string) {
  document.documentElement.dir = i18n.dir(lng);
  document.documentElement.lang = lng;
  // Write-through the last-known locale so the pre-paint head script
  // (theme-init.js) sets <html dir> correctly on the NEXT load. Hint only —
  // /me stays authoritative (CONVENTIONS §i18n).
  try {
    localStorage.setItem('locale', lng);
  } catch {
    /* private mode — skip the hint cache */
  }
}
applyDirection(i18n.language);
i18n.on('languageChanged', applyDirection);
// What a schema refuses, in the reader's words — one map for every form (lib/validation.ts).
installValidationMessages();

export default i18n;
